import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { EHRPosterPatchStatus } from "../../services/fhir/patientsummary/EHRPosterPatchStatus";
import { useFormatter } from "../../utils/useFormatter";
import { FormRadioButton, FormTextInput } from "../form";

interface IProps {
  data: any;
  index?: number;
  stop?: boolean;
  handleClose?: Function;
  updateParentStatus?: (status: string, note?: string) => Promise<void>;
}

const unit = [
  {
    value: "GRAN",
    label: "Grânulos",
    inputType: "number",
  },
  {
    value: "COM",
    label: "Comprimido",
    inputType: "number",
  },
  {
    value: "CAP",
    label: "Cápsula",
    inputType: "number",
  },
  {
    value: "SOL OR",
    label: "Solução Oral",
  },
  {
    value: "SOL INJ",
    label: "Solução Injetável",
  },
  {
    value: "SOL",
    label: "Solução",
  },

  {
    value: "SUSP",
    label: "Suspensão",
  },
  {
    value: "POM",
    label: "Pomada",
  },
  {
    value: "GEL",
    label: "Gel",
  },
  {
    value: "XAR",
    label: "Xarope",
  },
  {
    value: "INJ",
    label: "Injetável",
  },
  {
    value: "COL",
    label: "Colírio",
  },
  {
    value: "AER",
    label: "Aerossol",
  },
  {
    value: "SUP",
    label: "Supositório",
    inputType: "number",
  },
  {
    value: "AMP",
    label: "Ampola",
    inputType: "number",
  },
  {
    value: "CTG",
    label: "Cartucho",
    inputType: "number",
  },
  {
    value: "FLD",
    label: "Fluido",
  },
  {
    value: "CJ  ",
    label: "Conjunto",
    inputType: "number",
  },
  {
    value: "ENV",
    label: "Envelope ou Sachê",
  },
  {
    value: "BISN",
    label: "Bisnaga",
    inputType: "number",
  },
  {
    value: "DISP",
    label: "Dispositivo",
  },
  {
    value: "BALA",
    label: "Bala",
    inputType: "number",
  },
  {
    value: "LAQ",
    label: "Laca",
  },
];

export default function ControlMedication({
  data,
  index,
  stop,
  handleClose,
  updateParentStatus,
}: IProps) {
  const methods = useForm();
  const { handleSubmit, control } = methods;

  const watchStatusChoise = useWatch({
    name: "status",
    control,
  });

  const watchNote = useWatch({
    name: "note",
    control,
    defaultValue: data?.resource?.note?.find((noteItem: any) => 
      noteItem?.authorReference?.reference?.includes('Patient')
    )?.text || ""
  });

  const name = data?.resource?.medication?.concept?.text || "";
  const dosageUnity =
    data?.resource?.dosageInstruction?.[0]?.doseAndRate?.[0]?.doseQuantity
      ?.unit || "";
  const dosageQuantity =
    data?.resource?.dosageInstruction?.[0]?.doseAndRate?.[0]?.doseQuantity
      ?.value || "";

  const frequency =
    data?.resource?.dosageInstruction?.[0]?.timing?.repeat?.frequency || "";
  const period =
    data?.resource?.dosageInstruction?.[0]?.timing?.repeat?.period || "";
  const periodUnit =
    data?.resource?.dosageInstruction?.[0]?.timing?.repeat?.periodUnit || "";
  const duration =
    data?.resource?.dosageInstruction?.[0]?.timing?.repeat?.duration || "";
  const durationUnit =
    data?.resource?.dosageInstruction?.[0]?.timing?.repeat?.durationUnit || "";
  
  // Busca apenas a nota do paciente para comparação
  const patientNote = data?.resource?.note?.find((noteItem: any) => 
    noteItem?.authorReference?.reference?.includes('Patient')
  )?.text || "";

  const optionsChoise = [
    { display: "Sim, começei a tomar", value: "active" },
    { display: "Terminei de tomar", value: "completed" },
    { display: "Parei de tomar", value: "stopped" },
  ];

  const onSubmit = async (dataItens: any) => {
    try {
      // Se estamos no diálogo de parada e temos a função de atualização do componente pai
      if (stop && updateParentStatus) {
        // Neste caso, apenas chama a função do componente pai
        // O patch do note será feito pelo componente pai (HealthMedicineDetails)
        // Isso evita chamadas duplicadas e garante que apenas uma operação seja realizada
        await updateParentStatus(dataItens.status, dataItens.note);
      } else {
        // Comportamento padrão - apenas atualiza o status
        // Neste caso não há nota para ser adicionada, apenas mudança de status
        await EHRPosterPatchStatus({
          resourceType: data?.resource?.resourceType,
          resourceId: data?.resource?.id,
          status: dataItens.status,
        });
      }
      
      handleClose && handleClose();
      toast.success("Escolha registrada");
    } catch (err) {
      console.log("error:", err);
    }
  };

  return (
    <Accordion
      sx={{
        width: "100%",
        border: !stop ? "1px solid" : "none",
        borderColor: "neutral200.main",
        borderRadius: 1,
        ...(stop && {
          boxShadow: "none",
        }),
      }}
      defaultExpanded={index === 0 ? true : false}
    >
      {!stop && (
        <AccordionSummary
          expandIcon={!stop && <GridExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          {`${useFormatter.formatNameDisplay(name)}`}
        </AccordionSummary>
      )}

      <AccordionDetails>
        <Typography variant="body1">
          {dosageUnity && dosageQuantity
            ? `${dosageUnity}. (${dosageQuantity}un)`
            : ""}
        </Typography>
        <>
          <Typography variant="body1" color="neutral700.main">
            {frequency && period && periodUnit && duration && durationUnit
              ? ` ${frequency} ${
                  unit.find(
                    (el: any) =>
                      el.value ===
                      data?.resource?.dosageInstruction?.[0]?.doseAndRate?.[0]
                        ?.type?.coding?.[0]?.code
                  )?.label || ""
                }
                
             a cada ${period}${periodUnit} durante ${duration}${durationUnit} `
              : ""}
          </Typography>

          {data?.resource?.status === "draft" ||
          data?.resource?.status === "active" ? (
            <>
              <Stack width="100%" direction="column">
                <FormRadioButton
                  name="status"
                  label=""
                  defaultValue={!stop && data?.resource?.status}
                  control={control}
                  rowOrientation={false}
                  selectOptions={optionsChoise?.filter((e: any) =>
                    stop ? e?.value !== "active" : true
                  )}
                />
                {watchStatusChoise === "stopped" && (
                  <FormTextInput
                    multiline
                    rows={3}
                    label="Qual motivo?"
                    name="note"
                    control={control}
                    defaultValue={patientNote}
                  />
                )}
                {watchStatusChoise === "completed" && (
                  <FormTextInput
                    multiline
                    rows={3}
                    label="Observação (opcional)"
                    name="note"
                    control={control}
                    defaultValue={patientNote}
                  />
                )}
                {/* <CheckboxOptions
                  name="notificationChoise"
                  label="Deseja ativar notificações?"
                  control={control}
                  checked={watchNotificationChoise}
                /> */}
              </Stack>
              <Stack mt={1} direction="row" width="100%" justifyContent="right">
                <Button 
                  onClick={handleSubmit(onSubmit)} 
                  disabled={
                    !watchStatusChoise || 
                    (watchStatusChoise === "stopped" && (!watchNote || watchNote.trim() === ""))
                  }
                >
                  Enviar
                </Button>
              </Stack>
            </>
          ) : (
            <Stack width="100%">
              <Alert variant="outlined" severity="info">
                Controle de medicação encerrada
              </Alert>
            </Stack>
          )}
        </>
      </AccordionDetails>
    </Accordion>
  );
}
